import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Brain, Zap,
         TrendingUp, TrendingDown, Minus, RefreshCw, BarChart2, BookOpen, Info, X,
         Target, Gauge, Activity, Globe2, Landmark } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import TradingChart from '../charts/TradingChart';
import { formatDate } from '../../utils/formatters';
import { API_URL } from './learningData';
import { classifyVix } from '../../utils/playbookLogic';
import toast from 'react-hot-toast';
export function ActModule({ onBack }) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // view + selectedScenarioId persisted in URL so refresh restores position
  const view = searchParams.get('actView') || 'list';
  const setView = (v) => setSearchParams(p => { const n = new URLSearchParams(p); if (v === 'list') n.delete('actView'); else n.set('actView', v); return n; }, { replace: true });

  const [tradeType, setTradeType]     = useState('SWING');
  const [scenarios, setScenarios]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [riskProfile, setRiskProfile] = useState(null);
  const [history, setHistory]         = useState(null);
  const [reviewPlan, setReviewPlan]   = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [phase, setPhase]             = useState('pre');
  const [userAnswer, setUserAnswer]   = useState(null);
  const [allCandles, setAllCandles]   = useState([]);
  const [showFullChart, setShowFullChart] = useState(false);
  const [tradePlan, setTradePlan]     = useState({ decision: 'ENTER', entry: null, sl: null, target: null, conviction: 'MEDIUM', selectedTags: [] });
  const [immediateResult, setImmediateResult] = useState(null);
  const [outcomeTab, setOutcomeTab] = useState(null);
  const [dailyProgress, setDailyProgress] = useState({ swing: 0, intraday: 0 });
  const [relatedOpen, setRelatedOpen]     = useState(false);
  const replayRef = useRef(null);

  const fetchDaily = useCallback(async (type, { silent = false } = {}) => {
    if (!silent) { setLoading(true); setSelected(null); }
    try {
      const res = await fetch(`${API_URL}/learning/act/daily?type=${type}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setScenarios(Array.isArray(data) ? data : []);
    } catch { if (!silent) setScenarios([]); }
    finally { if (!silent) setLoading(false); }
  }, []);

  const fetchRiskProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/learning/risk-profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRiskProfile(data);
      }
    } catch(e) { console.error('Failed to fetch risk profile:', e); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/learning/act/history?limit=50`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch(e) { console.error('Failed to fetch history:', e); }
  }, []);

  const openPlanReview = useCallback(async (planId) => {
    setReviewLoading(true);
    setReviewPlan(null);
    setView('review');
    try {
      const res = await fetch(`${API_URL}/learning/act/plan/${planId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setReviewPlan(await res.json());
    } catch(e) { console.error('Failed to fetch plan review:', e); }
    finally { setReviewLoading(false); }
  }, []); // eslint-disable-line

  const fetchDailyProgress = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/learning/act/daily-progress`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDailyProgress(data);
      }
    } catch(e) { console.error('Failed to fetch daily progress:', e); }
  }, []);


  const handleToggle = (type) => {
    setTradeType(type);
    fetchDaily(type);
  };

  useEffect(() => {
    fetchDaily('SWING');
    fetchRiskProfile();
    fetchHistory();
    fetchDailyProgress();
  }, [fetchDaily, fetchRiskProfile, fetchHistory, fetchDailyProgress]);

  // Restore selected scenario from URL on refresh or direct deep-link
  useEffect(() => {
    const scenarioId = searchParams.get('actScenario');
    if (!scenarioId || selected) return;

    // Try today's list first
    const s = scenarios.find(sc => String(sc.id) === scenarioId);
    if (s) { openScenario(s); return; }

    // Not in today's list (historical or direct link) ? fetch by ID directly
    if (!loading) {
      setScenarioLoading(true);
      fetch(`${API_URL}/learning/act/${scenarioId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(fullScenario => {
          if (!fullScenario) return;
          const allCandleData = [
            ...(fullScenario.pre_candles || []),
            ...(fullScenario.outcome_candles || []),
          ];
          setSelected({ ...fullScenario, preLen: fullScenario.pre_candles?.length ?? allCandleData.length });
          setAllCandles(allCandleData);
          setPhase('pre');
          setUserAnswer(null);
          setShowFullChart(false);
          setTradePlan({ decision: 'ENTER', entry: fullScenario.entry_price, sl: null, target: null, qty: null, conviction: 'MEDIUM', reason: '', selectedTags: [] });
          setSearchParams(p => { const n = new URLSearchParams(p); n.set('actView', 'scenario'); return n; }, { replace: true });
        })
        .catch(() => {})
        .finally(() => setScenarioLoading(false));
    }
  }, [scenarios, searchParams, loading]);


  const openScenario = async (s) => {
    setSearchParams(p => { const n = new URLSearchParams(p); n.set('actView', 'scenario'); n.set('actScenario', s.id); return n; }, { replace: true });
    setPhase('pre');
    setUserAnswer(null);
    setShowFullChart(false);
    setOutcomeTab(null);
    // Initialize with entry price from scenario
    setTradePlan({ decision: 'ENTER', entry: s.entry_price, sl: null, target: null, qty: null, conviction: 'MEDIUM', reason: '', selectedTags: [] });

    // Fetch full scenario details including candles
    setScenarioLoading(true);
    try {
      const res = await fetch(`${API_URL}/learning/act/${s.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const fullScenario = await res.json();
        // Combine base with full data
        const scenarioWithCandles = {
          ...s,
          ...fullScenario,
          preLen: fullScenario.pre_candles?.length ?? allCandleData.length
        };
        setSelected(scenarioWithCandles);
        // Convert pre_candles + outcome_candles to allCandles
        const allCandleData = [
          ...(fullScenario.pre_candles || []),
          ...(fullScenario.outcome_candles || [])
        ];
        setAllCandles(allCandleData);
        // If already completed, restore the user's actual submitted plan so the
        // reveal shows their real SL/target/decision instead of a blank form.
        if (s.completed) {
          const up = fullScenario.user_plan;
          if (up) {
            setTradePlan({
              decision: up.decision || 'ENTER',
              entry: up.entry_price ?? s.entry_price,
              sl: up.stop_loss,
              target: up.target,
              qty: up.quantity,
              conviction: up.conviction || 'MEDIUM',
              reason: (up.selected_tags || []).join(','),
              selectedTags: up.selected_tags || [],
            });
          }
          setPhase('reveal');
        }
      }
    } catch(e) {
      console.error('Failed to load scenario details:', e);
      setSelected(s);
      setAllCandles([]);
    } finally {
      setScenarioLoading(false);
    }
  };

  // Live trade plan calculations
  // Fixed ₹25,000 investment per ACT trade — qty is always auto-derived
  const fixedQty = (() => {
    const e = Number(tradePlan.entry) || Number(selected?.entry_price);
    if (!e) return null;
    return Math.floor(25000 / e);
  })();

  const isShort = selected?.trade_type === 'INTRADAY' && Number(tradePlan.sl) > Number(tradePlan.entry);

  const liveRR = (() => {
    const e = Number(tradePlan.entry), s = Number(tradePlan.sl), t = Number(tradePlan.target);
    if (!e || !s || !t) return null;
    if (isShort) {
      if (s <= e || t >= e) return null;
      return (e - t) / (s - e);
    }
    if (s >= e || t <= e) return null;
    return (t - e) / (e - s);
  })();

  const liveRiskAmount = (() => {
    const e = Number(tradePlan.entry), s = Number(tradePlan.sl), q = fixedQty;
    if (!e || !s || !q) return null;
    if (isShort) {
      if (s <= e) return null;
      return q * (s - e);
    }
    if (s >= e) return null;
    return q * (e - s);
  })();

  const liveRiskPct = (() => {
    const positionValue = fixedQty * Number(tradePlan.entry);
    if (!liveRiskAmount || !positionValue) return null;
    return (liveRiskAmount / positionValue) * 100;
  })();

// Inline validation ? returns error string or null
  const getPlanError = () => {
    if (tradePlan.decision === 'SKIP' && !(tradePlan.selectedTags?.length))
      return 'Select at least one reason tag before submitting.';
    if (tradePlan.decision === 'ENTER') {
      if (!(tradePlan.selectedTags?.length))
        return 'Select at least one reason tag for your entry.';
      if (!tradePlan.sl || !tradePlan.target)
        return 'Fill in stop loss and target.';
      // SWING is long-only today, so SL/target must be below/above entry.
      // INTRADAY can be long or short, so this directional check doesn't apply yet.
      if (selected?.trade_type !== 'INTRADAY') {
        if (Number(tradePlan.sl) >= Number(tradePlan.entry || selected?.entry_price))
          return 'Stop loss must be below entry price (target below entry is not allowed in Swing).';
        if (Number(tradePlan.target) <= Number(tradePlan.entry || selected?.entry_price))
          return 'Target must be above entry price.';
      }
    }
    return null;
  };

  const submitTradePlan = async (skipNavigation = false) => {
    if (!selected || !riskProfile) return;
    const err = getPlanError();
    if (err) { toast.error(err); return; }
    const token = localStorage.getItem('token');
    try {
      const entryPrice = tradePlan.entry || selected?.entry_price;
      const payload = {
        scenario_id: selected.id,
        user_decision: tradePlan.decision,
        ...(tradePlan.decision === 'ENTER' && {
          planned_entry_price: entryPrice,
          planned_stop_loss: tradePlan.sl,
          planned_target: tradePlan.target,
          planned_quantity: fixedQty,
          conviction_level: tradePlan.conviction,
          user_reason: (tradePlan.selectedTags || []).join(','),
          selected_tags: tradePlan.selectedTags || [],
        }),
        ...(tradePlan.decision === 'SKIP' && { user_reason: (tradePlan.selectedTags || []).join(','), selected_tags: tradePlan.selectedTags || [] })
      };
      const res = await fetch(`${API_URL}/learning/act/${selected.id}/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.immediate_score != null) {
          setImmediateResult({
            score:         data.immediate_score,
            label:         data.label,
            flags:         data.flags || [],
            feedback:      data.feedback || [],
            setup_strength: data.setup_strength,
            contradictions: data.contradictions || [],
            valid_tags:    data.valid_tags || [],
            setup_read:    data.setup_read || null,
            plan_challenge: data.plan_challenge || null,
            tip:           data.tip || null,
            outcome_alignment: data.outcome_alignment || null,
          });
        }
        fetchDailyProgress();
        fetchHistory();
        fetchDaily(selected?.trade_type || tradeType, { silent: true });
        if (skipNavigation) {
          handleReveal();   // only reveal AFTER confirmed save
        } else {
          setSelected(null);
          setSearchParams(p => { const n = new URLSearchParams(p); n.delete('actView'); n.delete('actScenario'); return n; }, { replace: true });
        }
        return true;
      } else if (res.status === 409) {
        // Already submitted — treat as success and proceed to reveal
        fetchDailyProgress();
        fetchHistory();
        fetchDaily(selected?.trade_type || tradeType, { silent: true });
        if (skipNavigation) handleReveal();
        return true;
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Submission failed. Please check your inputs.');
        return false;
      }
    } catch(e) { console.error('Failed to submit plan:', e); return false; }
  };

  const handleAnswer = (answer) => {
    setUserAnswer(answer);
    setPhase('answered');
    const dec = answer === 'Enter' ? 'ENTER' : 'SKIP';
    setTradePlan(p => ({
      ...p,
      decision: dec,
      entry: selected?.entry_price || p.entry,
      selectedTags: [],
    }));
  };

  const handleReveal = () => setPhase('reveal');

  const dirIcon = (d) => d === 'bullish'
    ? <TrendingUp  className="w-3.5 h-3.5 text-emerald-400" />
    : d === 'bearish'
    ? <TrendingDown className="w-3.5 h-3.5 text-red-400" />
    : <Minus className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />;

  // ?? Context panel helpers ?????????????????????????????????????????????????
  const metricColor = (type, val) => {
    if (val == null) return colors.secondaryText;
    switch (type) {
      case 'rsi':    return val > 70 ? '#ef4444' : val < 30 ? '#ef4444' : val <= 65 ? '#16C784' : '#f59e0b';
      case 'vol':    return val >= 2 ? '#16C784' : val >= 1.2 ? '#f59e0b' : '#ef4444';
      case 'macd':   return val === 'BULL' ? '#16C784' : val === 'BEAR' ? '#ef4444' : '#f59e0b';
      case 'atr':    return val < 1.5 ? '#16C784' : val < 3 ? '#f59e0b' : '#ef4444';
      case 'bb':     return val === 'MID_BAND' ? '#16C784' : val === 'NEAR_UPPER' ? '#f59e0b' : val === 'ABOVE_UPPER' ? '#ef4444' : '#f59e0b';
      case 'w52':    return val >= 40 && val <= 80 ? '#16C784' : val > 80 && val <= 95 ? '#f59e0b' : '#ef4444';
      case 'chg':    return val > 1 ? '#16C784' : val >= 0 ? '#f59e0b' : '#ef4444';
      case 'vwap':   return val === 'above_vwap' ? '#16C784' : val === 'below_vwap' ? '#ef4444' : '#f59e0b';
      case 'orb':    return val === 'above_orb' ? '#16C784' : val === 'below_orb' ? '#f59e0b' : '#f59e0b';
      case 'trend':  return val === 'UPTREND' ? '#16C784' : val === 'DOWNTREND' ? '#ef4444' : '#f59e0b';
      default:       return colors.secondaryText;
    }
  };

  const metricVerdict = (type, val) => {
    if (val == null) return null;
    switch (type) {
      case 'rsi':
        if (val > 70)            return 'Overbought — reversal risk';
        if (val < 30)            return 'Oversold — bounce possible';
        if (val > 65)            return 'Elevated — watch closely';
        return                          'Healthy range';
      case 'vol':
        if (val >= 2)            return 'Strong confirmation';
        if (val >= 1.2)          return 'Moderate — acceptable';
        return                          'Weak — low conviction';
      case 'macd':
        if (val === 'BULL')      return 'Momentum is bullish';
        if (val === 'BEAR')      return 'Momentum is bearish';
        return                          'No clear momentum';
      case 'atr':
        if (val < 1.5)           return 'Low volatility — tight SL';
        if (val < 3)             return 'Normal — plan SL carefully';
        return                          'High volatility — wider SL needed';
      case 'bb':
        if (val === 'MID_BAND')  return 'Room to run both ways';
        if (val === 'NEAR_UPPER')return 'Near resistance band';
        if (val === 'ABOVE_UPPER')return 'Overextended — breakout or fade';
        return                          'Near support band';
      case 'w52':
        if (val >= 40 && val <= 80) return 'Mid-range — room to move';
        if (val > 80 && val <= 95)  return 'Near 52W high — breakout zone';
        if (val > 95)               return 'At 52W high — extended';
        return                             'Near 52W low — risky';
      case 'chg':
        if (val > 1)             return 'Strong day — momentum';
        if (val >= 0)            return 'Mild gain — neutral';
        return                          'Down day — weak';
      case 'trend':
        if (val === 'UPTREND')   return 'Trade with the trend';
        if (val === 'DOWNTREND') return 'Counter-trend — higher risk';
        return                          'Sideways — no clear edge';
      case 'vwap':
        if (val === 'above_vwap') return 'Price above VWAP — bullish';
        if (val === 'below_vwap') return 'Price below VWAP — bearish';
        return                           'At VWAP — neutral';
      case 'orb':
        if (val === 'above_orb')  return 'Above opening range — breakout';
        if (val === 'below_orb')  return 'Below ORB — wait for reclaim';
        return                           'At opening range boundary';
      default: return null;
    }
  };

  const ENTER_TAGS = [
    { id: 'breakout_volume',  label: 'Breakout with volume' },
    { id: 'trend_aligned',    label: 'Trend aligned' },
    { id: 'near_support',     label: 'Near support' },
    { id: 'rsi_in_range',     label: 'RSI in range' },
    { id: 'macd_crossover',   label: 'MACD crossover' },
    { id: 'scanner_signal',   label: 'Scanner signal' },
  ];

  const SKIP_TAGS = [
    { id: 'volume_too_low',    label: 'Volume too low' },
    { id: 'trend_downtrend',   label: 'Trend is down' },
    { id: 'rsi_overbought',    label: 'RSI overbought' },
    { id: 'macd_bearish',      label: 'MACD bearish' },
    { id: 'near_resistance',   label: 'Near resistance' },
    { id: 'extended_from_sma', label: 'Chasing — too far from SMA' },
    { id: 'missed_move',       label: 'Already missed the move' },
    { id: 'poor_rr',           label: 'Poor R:R on this setup' },
    { id: 'no_stop_level',     label: 'No clean SL level' },
    { id: 'weak_setup',        label: 'Setup looks weak' },
    { id: 'sector_headwind',   label: 'Sector / macro against it' },
    { id: 'strategy_not_clear',label: 'No clear strategy for this' },
  ];


  const SIGNAL_LABELS = {
    // Swing
    BREAKOUT_VOLUME_SPIKE:  'Breakout + Volume Surge',
    BREAKOUT_20D_HIGH:      '20-Day Breakout',
    VOLUME_SPIKE:           'Volume Spike',
    BULLISH_ENGULFING:      'Bullish Engulfing',
    // Intraday
    INTRADAY_BREAKOUT:      'Intraday Breakout',
    VOLUME_SURGE:           'Volume Surge',
    ORB_BREAKOUT:           'ORB Breakout',
    GAP_UP_HOLD:            'Gap Up Hold',
    INTRADAY_REVERSAL:      'Intraday Reversal',
    INTRADAY_MOMENTUM:      'Momentum',
    SUPPORT_BOUNCE:         'Support Bounce',
    PREV_HIGH_BREAKOUT:     'Prev High Breakout',
    BB_SQUEEZE_BREAK:       'BB Squeeze Break',
    VWAP_RECLAIM:           'VWAP Reclaim',
  };

  const MetricTile = ({ label, value, display, colorType }) => {
    const col     = metricColor(colorType, value);
    const verdict = metricVerdict(colorType, value);
    return (
      <div className="flex-1 min-w-0 p-2.5 rounded-xl flex flex-col gap-0.5"
        style={{ backgroundColor: colors.inputBg }}>
        <span className="text-[12px] uppercase tracking-wide font-medium"
          style={{ color: colors.secondaryText }}>{label}</span>
        <span className="text-xs font-bold truncate" style={{ color: col }}>{display ?? '?'}</span>
        {verdict && (
          <span className="text-[10px] leading-tight" style={{ color: col, opacity: 0.75 }}>{verdict}</span>
        )}
      </div>
    );
  };

  const sentimentColor = (label) => {
    if (!label) return colors.secondaryText;
    const normalized = String(label).toUpperCase();
    if (normalized.includes('RISK_ON') || normalized.includes('POSITIVE') || normalized.includes('BULL')) return '#16C784';
    if (normalized.includes('RISK_OFF') || normalized.includes('NEGATIVE') || normalized.includes('BEAR')) return '#EF4444';
    return '#f59e0b';
  };

  // ── Section helpers ────────────────────────────────────────────────────────
  const SECTION_ICONS = { 1: Globe2, 2: Landmark, 3: Activity };
  const SectionCard = ({ label, accent, children }) => {
    const sectionNum = parseInt((label.match(/Section (\d)/) || [])[1], 10);
    const Icon = SECTION_ICONS[sectionNum];
    return (
      <div className="app-card rounded-2xl border p-3.5 space-y-3"
        style={{ background: `linear-gradient(135deg, ${accent || colors.accent}18 0%, ${accent || colors.accent}06 100%)`, borderColor: `${accent || colors.accent}35` }}>
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent || colors.accent }} />}
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent || colors.accent }}>{label}</p>
        </div>
        {children}
      </div>
    );
  };

  const gapMeta = {
    strong_up:   { text: 'Strong Gap Up',   sub: 'Gap ≥ +1% — institutions drove overnight. Do not chase open.', color: '#16C784' },
    mild_up:     { text: 'Mild Gap Up',     sub: 'Gap +0.3–1% — mild positive bias. Watch first 15 min.',        color: '#16C784' },
    flat:        { text: 'Flat Open',       sub: 'Gap within ±0.3% — no overnight bias. Open behavior decides.', color: '#f59e0b' },
    mild_down:   { text: 'Mild Gap Down',   sub: 'Gap −0.3–1% — mild negative bias. Watch for stabilisation.',   color: '#EF4444' },
    strong_down: { text: 'Strong Gap Down', sub: 'Gap ≤ −1% — aggressive longs are high risk at open.',          color: '#EF4444' },
  };
  const openMeta = {
    sustained: { text: 'Sustained',     sub: 'Gap held — price continued in gap direction. Trend day.',               color: '#16C784' },
    reversal:  { text: 'Sharp Reversal',sub: 'Price went opposite to expectation. Original bias was wrong.',           color: '#EF4444' },
    mixed:     { text: 'Mixed Signals', sub: 'Global and FII cues conflicted. Watch price action for real direction.', color: '#f59e0b' },
    range:     { text: 'Range-bound',   sub: 'Flat open, no directional bias. Wait for breakout from morning range.',  color: '#8b5cf6' },
    gap_fill:  { text: 'Gap Fill',      sub: 'Opened with gap but moved to fill it. Fading the gap is the play.',     color: '#f97316' },
  };
  const fiiBiasMeta = {
    buy:     { text: 'Buying',  color: '#16C784' },
    sell:    { text: 'Selling', color: '#EF4444' },
    neutral: { text: 'Neutral', color: '#f59e0b' },
  };

  // ── All-in-one scenario context — 4 sections ───────────────────────────────
  const ScenarioContext = ({ s, context, revealed }) => {
    const [infoPopup, setInfoPopup] = useState(null); // { title, body } | null
    if (!context) return null;
    const scenarioIsIntraday = s?.trade_type === 'INTRADAY';
    const regime  = context.intraday_open || null;
    const mood    = context.market_mood   || {};
    const indices = context.global_indices    || [];

    const InfoBadge = ({ title, points, keySignal }) => (
      <button
        type="button"
        onClick={() => setInfoPopup({ title, points, keySignal })}
        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        aria-label={`How ${title} is calculated`}
      >
        <Info className="w-3 h-3" style={{ color: colors.secondaryText }} />
      </button>
    );

    const InfoPopupModal = () => {
      if (!infoPopup) return null;
      return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setInfoPopup(null)}>
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} />
          <div className="relative w-full max-w-sm rounded-2xl p-5 space-y-3 text-[12px] leading-relaxed"
            style={{ background: colors.cardBg, border: '1px solid rgba(99,102,241,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-bold" style={{ color: '#6366f1' }}>{infoPopup.title}</p>
              <button onClick={() => setInfoPopup(null)} className="text-[18px] leading-none" style={{ color: colors.secondaryText }}>×</button>
            </div>
            {infoPopup.points.map((pt, i) => (
              <p key={i} style={{ color: colors.secondaryText }}>
                <span className="font-semibold" style={{ color: colors.primaryText }}>{pt.label}</span> {pt.text}
              </p>
            ))}
            {infoPopup.keySignal && (
              <p className="text-[11px] font-semibold px-3 py-2 rounded-xl" style={{ background: '#f59e0b18', color: '#f59e0b' }}>
                {infoPopup.keySignal}
              </p>
            )}
          </div>
        </div>
      );
    };

    const usIdx   = indices.filter(i => ['^GSPC','^DJI','^IXIC'].includes(i.symbol));
    const dxy     = indices.filter(i => i.symbol === 'DX-Y.NYB');
    const asiaIdx = indices.filter(i => ['^N225','^HSI'].includes(i.symbol));
    const euIdx   = indices.filter(i => ['^FTSE','^GDAXI'].includes(i.symbol));

    const fmtIdxDate = (iso) => {
      if (!iso) return '—';
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    };

    const IndexTile = ({ idx }) => {
      const pos = idx.change_pct >= 0;
      const tint = idx.change_pct == null ? colors.secondaryText : pos ? '#16C784' : '#EF4444';
      return (
        <div className="app-card rounded-xl p-2.5" style={{ background: `linear-gradient(135deg, ${tint}12 0%, ${tint}04 100%)`, border: `1px solid ${tint}25` }}>
          <p className="text-[11px] font-semibold truncate" style={{ color: colors.primaryText }}>{idx.name}</p>
          <p className="text-[9px] mb-1" style={{ color: colors.secondaryText }}>{fmtIdxDate(idx.price_date || idx.snapshot_date)}</p>
          <p className="text-[15px] font-bold tabular-nums" style={{ color: tint }}>
            {idx.change_pct == null ? '—' : `${pos ? '+' : ''}${idx.change_pct.toFixed(2)}%`}
          </p>
        </div>
      );
    };

    const RegimePill = ({ label, value, color }) => (
      <div className="flex items-center justify-between rounded-xl px-3 py-2" style={{ backgroundColor: colors.inputBg }}>
        <span className="text-[11px]" style={{ color: colors.secondaryText }}>{label}</span>
        <span className="text-[12px] font-bold" style={{ color }}>{value}</span>
      </div>
    );

    return (
      <div className="space-y-3">

        {/* ── Section 1: Global Cues ── */}
        <SectionCard label="Section 1 — Global Cues" accent="#6366f1">
          {/* Mood row */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[13px] font-bold" style={{ color: mood.color || colors.primaryText }}>
                {mood.label || 'Unknown'}
              </p>
              {mood.summary && <p className="text-[11px] mt-0.5 leading-snug" style={{ color: colors.secondaryText }}>{mood.summary}</p>}
            </div>
            <span className="text-[10px] px-2 py-1 rounded-lg flex-shrink-0 ml-2" style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
              {context.signal_date}
            </span>
          </div>

          {/* Index grid */}
          {context.global_cues_available && indices.length > 0 ? (
            <div className="space-y-2">
              {usIdx.length > 0 && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>US — prev day close</p>
                  <div className={`grid gap-1.5 grid-cols-3`}>{usIdx.map(i => <IndexTile key={i.symbol} idx={i} />)}</div>
                </div>
              )}
              {asiaIdx.length > 0 && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>Asia — morning signal</p>
                  <div className={`grid gap-1.5 grid-cols-2`}>{asiaIdx.map(i => <IndexTile key={i.symbol} idx={i} />)}</div>
                </div>
              )}
              {(euIdx.length > 0 || dxy.length > 0) && (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>Europe + Dollar</p>
                  <div className={`grid gap-1.5 grid-cols-3`}>{[...euIdx, ...dxy].map(i => <IndexTile key={i.symbol} idx={i} />)}</div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[11px]" style={{ color: colors.secondaryText }}>No global cues snapshot within 7 days of signal date.</p>
          )}

          {/* FII + DII — own row */}
          {regime && (regime.fii_bias || regime.dii_net != null) && (
            <div className="grid grid-cols-2 gap-1.5">
              {regime.fii_bias && (
                <RegimePill
                  label="FII flow"
                  value={`${regime.fii_net >= 0 ? 'NET LONG' : 'NET SHORT'}${regime.fii_net != null ? ` ${Math.abs(regime.fii_net).toLocaleString('en-IN')}` : ''}`}
                  color={fiiBiasMeta[regime.fii_bias]?.color || colors.secondaryText}
                />
              )}
              {regime.dii_net != null && (
                <RegimePill
                  label="DII flow"
                  value={`${regime.dii_net >= 0 ? 'NET LONG' : 'NET SHORT'} ${Math.abs(regime.dii_net).toLocaleString('en-IN')}`}
                  color={regime.dii_net > 0 ? '#16C784' : regime.dii_net < 0 ? '#EF4444' : '#f59e0b'}
                />
              )}
            </div>
          )}
          {regime && (regime.fii_bias && regime.dii_net != null) && (
            <p className="text-[10px] px-1" style={{ color: colors.secondaryText }}>
              FII and DII trade at very different volumes — these counts aren't meant to be compared directly, only the direction (long/short) matters.
            </p>
          )}
        </SectionCard>

        {/* ── Section 2: Market Regime ── */}
        {regime && (
          <SectionCard label={scenarioIsIntraday ? 'Section 2 — Market Regime (Intraday)' : 'Section 2 — Market Regime (Swing)'} accent="#f59e0b">

            {/* One-line regime summary */}
            {regime.open_behavior && (
              <p className="text-[11px] font-semibold" style={{ color: openMeta[regime.open_behavior]?.color || colors.secondaryText }}>
                {regime.global_bias ? `${regime.global_bias.charAt(0).toUpperCase()}${regime.global_bias.slice(1)} bias · ` : ''}{openMeta[regime.open_behavior]?.text || regime.open_behavior}
              </p>
            )}

            {/* Alert strip — expiry / conflict / high VIX */}
            {(regime.is_expiry || regime.conflict || (regime.vix != null && regime.vix > 20)) && (
              <div className="flex flex-wrap gap-1.5">
                {regime.is_expiry && (
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: '#f9731620', color: '#f97316', border: '1px solid #f9731640' }}>
                    Expiry Day — premium risk
                  </span>
                )}
                {regime.conflict && (
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }}>
                    Conflict — choppy day
                  </span>
                )}
                {regime.vix != null && regime.vix > 20 && (
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40' }}>
                    VIX {regime.vix.toFixed(1)} — elevated volatility
                  </span>
                )}
              </div>
            )}

            {/* Intraday-only: gap type + open behavior — Nifty (index-level) */}
            {scenarioIsIntraday && regime.gap_type && (
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>Nifty — index level</p>
                <div className="grid grid-cols-2 gap-1.5">
                <div className="rounded-xl p-2.5" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>Nifty Gap at Open</p>
                  <p className="text-[13px] font-bold mt-0.5" style={{ color: gapMeta[regime.gap_type]?.color }}>
                    {regime.gap_pct != null ? `${regime.gap_pct >= 0 ? '+' : ''}${regime.gap_pct.toFixed(2)}%` : '—'}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: gapMeta[regime.gap_type]?.color }}>
                    {gapMeta[regime.gap_type]?.text}
                  </p>
                  <p className="text-[10px] mt-1 leading-snug" style={{ color: colors.secondaryText }}>
                    {gapMeta[regime.gap_type]?.sub}
                  </p>
                </div>
                <div className="rounded-xl p-2.5" style={{ backgroundColor: colors.inputBg }}>
                  <div className="flex items-center gap-1">
                    <p className="text-[10px]" style={{ color: colors.secondaryText }}>Nifty Open Behavior</p>
                    <InfoBadge
                      title="Nifty Open Behavior"
                      points={[
                        { label: 'How it works.', text: 'Compares the expected direction (from global cues + FII flow) against the actual Nifty price direction.' },
                        { label: 'Sustained', text: '= price direction matched what global cues + FII flow expected.' },
                        { label: 'Sharp Reversal', text: '= price went opposite to what was expected.' },
                        { label: 'Mixed Signals', text: '= the macro cues themselves disagreed with each other.' },
                        { label: 'Range-bound', text: '= flat open, no directional bias either way.' },
                        { label: 'Gap Fill', text: '= gapped at open but faded back toward flat.' },
                      ]}
                    />
                  </div>
                  <p className="text-[13px] font-bold mt-0.5" style={{ color: openMeta[regime.open_behavior]?.color }}>
                    {openMeta[regime.open_behavior]?.text || regime.open_behavior || '—'}
                  </p>
                  <p className="text-[10px] mt-1 leading-snug" style={{ color: colors.secondaryText }}>
                    {openMeta[regime.open_behavior]?.sub}
                  </p>
                </div>
                </div>
              </div>
            )}

            {/* Intraday-only: gap type + open behavior — this stock, own candles */}
            {scenarioIsIntraday && regime.stock_open?.data_available && (
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>{s?.symbol} — stock level</p>
                <div className="grid grid-cols-2 gap-1.5">
                <div className="rounded-xl p-2.5" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>{s?.symbol} Gap at Open</p>
                  <p className="text-[13px] font-bold mt-0.5" style={{ color: gapMeta[regime.stock_open.gap_type]?.color }}>
                    {regime.stock_open.gap_value != null ? `${regime.stock_open.gap_value >= 0 ? '+' : ''}${regime.stock_open.gap_value.toFixed(2)}%` : '—'}
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: gapMeta[regime.stock_open.gap_type]?.color }}>
                    {gapMeta[regime.stock_open.gap_type]?.text}
                  </p>
                </div>
                <div className="rounded-xl p-2.5" style={{ backgroundColor: colors.inputBg }}>
                  <div className="flex items-center gap-1">
                    <p className="text-[10px]" style={{ color: colors.secondaryText }}>{s?.symbol} Open Behavior</p>
                    <InfoBadge
                      title={`${s?.symbol} Open Behavior`}
                      points={[
                        { label: 'How it works.', text: `Based only on ${s?.symbol}'s own price action — no global cues or FII/DII involved. Compares the stock's gap direction against its first 30 min (9:15–9:45) move.` },
                        { label: 'Sustained', text: '= gap held and price kept moving in that direction.' },
                        { label: 'Sharp Reversal', text: '= price moved hard on both sides of the open — a genuine round trip.' },
                        { label: 'Gap Fill', text: '= gapped at open but drifted back toward flat.' },
                        { label: 'Range-bound', text: '= no clean gap or decisive move either way.' },
                      ]}
                    />
                  </div>
                  <p className="text-[13px] font-bold mt-0.5" style={{ color: openMeta[regime.stock_open.open_behavior]?.color }}>
                    {openMeta[regime.stock_open.open_behavior]?.text || regime.stock_open.open_behavior || '—'}
                  </p>
                </div>
                </div>
              </div>
            )}

            {/* VIX bar (always shown when available) */}
            {regime.vix != null && (() => {
              const vixLevel = classifyVix(regime.vix) || { color: colors.secondaryText, label: '' };
              return (
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.25)' }}>Volatility</p>
                  <div className="rounded-xl p-2.5" style={{ backgroundColor: colors.inputBg }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px]" style={{ color: colors.secondaryText }}>India VIX</p>
                      <p className="text-[13px] font-bold tabular-nums" style={{ color: vixLevel.color }}>
                        {regime.vix.toFixed(1)}
                        <span className="text-[10px] font-normal ml-1" style={{ color: colors.secondaryText }}>
                          — {vixLevel.label.toLowerCase()}
                        </span>
                      </p>
                    </div>
                    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#ffffff15' }}>
                      <div className="h-full rounded-full"
                        style={{
                          width: `${Math.min(100, (regime.vix / 35) * 100)}%`,
                          background: vixLevel.color,
                        }} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </SectionCard>
        )}

        {/* ── Section 3: Stock Signals ── */}
        <SectionCard label="Section 3 — Stock Signals" accent="#16C784">
          {/* One-line direction summary */}
          {s.direction && (
            <p className="text-[11px] font-semibold" style={{ color: s.direction === 'bullish' ? '#16C784' : s.direction === 'bearish' ? '#ef4444' : colors.secondaryText }}>
              {s.direction.charAt(0).toUpperCase()}{s.direction.slice(1)} setup · {s.trend ? s.trend.replace('TREND',' trend').toLowerCase() : 'trend n/a'}
            </p>
          )}

          {/* Scanner badge + signal time */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide"
              style={{ background: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b30' }}>
              {SIGNAL_LABELS[s.scanner_name] || s.scanner_name.replace(/_/g, ' ')}
            </span>
            <span className="text-[11px]" style={{ color: colors.secondaryText }}>triggered · {s.signal_date}</span>
          </div>

          {/* Intraday signal timing strip */}
          {scenarioIsIntraday && s.signal_time && (() => {
            const slotMeta = {
              opening:     { label: 'Opening', color: '#f59e0b' },
              mid_morning: { label: 'Mid Morning', color: '#3b82f6' },
              lunch_chop:  { label: 'Lunch Chop', color: '#8b5cf6' },
              power_hour:  { label: 'Power Hour', color: '#ef4444' },
            };
            const slot = slotMeta[s.time_slot] || { label: s.time_slot, color: '#6b7280' };
            const totalMins = 375; // 9:15 → 15:30
            const remain = s.session_remaining ?? 0;
            const elapsed = totalMins - remain;
            const pct = Math.min(100, Math.max(0, (elapsed / totalMins) * 100));
            const remainH = Math.floor(remain / 60);
            const remainM = remain % 60;
            const remainStr = remainH > 0 ? `${remainH}h ${remainM}m left` : `${remainM}m left`;
            // Format signal_time "HH:MM:SS" → "HH:MM AM/PM"
            const [hh, mm] = (s.signal_time || '').split(':');
            const hour = parseInt(hh, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const h12 = hour % 12 || 12;
            const timeStr = `${h12}:${mm} ${ampm}`;
            return (
              <div className="rounded-xl p-3 flex flex-col gap-2"
                style={{ background: '#ffffff08', border: '1px solid #ffffff12' }}>
                {/* Time + slot */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-bold tabular-nums" style={{ color: colors.primaryText }}>
                      {timeStr}
                    </span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide"
                      style={{ background: `${slot.color}20`, color: slot.color, border: `1px solid ${slot.color}40` }}>
                      {slot.label}
                    </span>
                  </div>
                  <span className="text-[11px]" style={{ color: colors.secondaryText }}>{remainStr}</span>
                </div>
                {/* Session progress bar */}
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#ffffff15' }}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: slot.color }} />
                </div>
              </div>
            );
          })()}

          {/* Metric rows */}
          {s.trade_type === 'INTRADAY' ? (
            <div className="grid grid-cols-3 gap-1.5">
              <MetricTile label="RSI (Intraday)"    value={s.intraday_rsi}      colorType="rsi"  display={s.intraday_rsi != null ? s.intraday_rsi.toFixed(1) : null} />
              <MetricTile label="Volume (Intraday)" value={s.intraday_vol_ratio} colorType="vol" display={s.intraday_vol_ratio != null ? `${Number(s.intraday_vol_ratio).toFixed(1)}x` : null} />
              <MetricTile label="VWAP"       value={s.vwap_position}     colorType="vwap" display={s.vwap_position ? (s.vwap_position === 'above_vwap' ? 'ABOVE' : s.vwap_position === 'below_vwap' ? 'BELOW' : 'AT') : null} />
              <MetricTile label="vs ORB"     value={s.price_vs_orb}      colorType="orb"  display={s.price_vs_orb ? (s.price_vs_orb === 'above_orb' ? 'ABOVE' : s.price_vs_orb === 'below_orb' ? 'BELOW' : 'AT') : null} />
              <MetricTile label="Chg from Open" value={s.change_from_open_pct} colorType="chg" display={s.change_from_open_pct != null ? `${s.change_from_open_pct >= 0 ? '+' : ''}${s.change_from_open_pct.toFixed(2)}%` : null} />
              <MetricTile label="ATR (Intraday)" value={s.intraday_atr_pct} colorType="atr" display={s.intraday_atr_pct != null ? `${s.intraday_atr_pct.toFixed(2)}%` : null} />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              <MetricTile label="RSI 14"    value={s.rsi}            colorType="rsi"   display={s.rsi != null ? s.rsi.toFixed(1) : null} />
              <MetricTile label="Volume"    value={s.volume_ratio}   colorType="vol"   display={s.volume_ratio != null ? `${s.volume_ratio.toFixed(1)}x` : null} />
              <MetricTile label="Trend"     value={s.trend}          colorType="trend" display={s.trend ? s.trend.replace('TREND','').replace('SIDEWAYS','SIDE') : null} />
              <MetricTile label="MACD"      value={s.macd_direction} colorType="macd"  display={s.macd_direction} />
              <MetricTile label="ATR"       value={s.atr_pct}         colorType="atr"  display={s.atr_pct != null ? `${s.atr_pct.toFixed(1)}%` : null} />
              <MetricTile label="BB Band"   value={s.bb_position}     colorType="bb"   display={s.bb_position ? s.bb_position.replace(/_/g,' ') : null} />
            </div>
          )}

          {/* Setup type — hidden before reveal */}
          {revealed && regime?.setup_type && (
            <div className="rounded-xl px-3 py-2.5 flex items-center justify-between"
              style={{ background: `${colors.accent}12`, border: `1px solid ${colors.accent}30` }}>
              <span className="text-[11px]" style={{ color: colors.secondaryText }}>{scenarioIsIntraday ? 'Nifty regime setup type' : 'Regime setup type'}</span>
              <span className="text-[12px] font-bold" style={{ color: colors.accent }}>
                {regime.setup_type.replace(/_/g, ' ')}
              </span>
            </div>
          )}
          {revealed && scenarioIsIntraday && regime?.stock_open?.data_available && regime.stock_open.setup_type && (
            <div className="rounded-xl px-3 py-2.5 flex items-center justify-between"
              style={{ background: `${colors.accent}12`, border: `1px solid ${colors.accent}30` }}>
              <span className="text-[11px]" style={{ color: colors.secondaryText }}>{s?.symbol} regime setup type</span>
              <span className="text-[12px] font-bold" style={{ color: colors.accent }}>
                {regime.stock_open.setup_type.replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </SectionCard>

        <InfoPopupModal />
      </div>
    );
  };

  // ?? Enhanced scenario view with trade planning ??????????????????????????
  if (view === 'scenario' && selected) {
    const marketRewarded = selected.correct_answer; // what the market rewarded — not "correct"
    const baselinePct = selected.outcome_pct;
    // When the user entered, show their actual outcome % (not the baseline scenario %)
    const userPct = (() => {
      if (tradePlan.decision !== 'ENTER' || !tradePlan.entry) return null;
      const entry = Number(tradePlan.entry);
      if (!entry) return null;
      // userPlanOutcome computed below — read from allCandles directly here
      const outcomeCandles = allCandles.slice(selected.preLen ?? allCandles.length);
      for (let i = 0; i < outcomeCandles.length; i++) {
        const c = outcomeCandles[i];
        if (tradePlan.target && c.high >= Number(tradePlan.target))
          return ((Number(tradePlan.target) - entry) / entry * 100);
        if (tradePlan.sl && c.low <= Number(tradePlan.sl))
          return ((Number(tradePlan.sl) - entry) / entry * 100);
      }
      const lastClose = outcomeCandles[outcomeCandles.length - 1]?.close;
      return lastClose ? ((lastClose - entry) / entry * 100) : null;
    })();
    const pct      = userPct != null ? userPct : baselinePct;
    const pctStr   = pct != null ? (pct >= 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`) : null;
    const pctColor = pct == null ? colors.secondaryText : pct > 0 ? '#16C784' : '#ef4444';
    const pnlAmount = pct != null && fixedQty && tradePlan.entry
      ? (pct / 100) * fixedQty * Number(tradePlan.entry)
      : null;
    const pnlAmountStr = pnlAmount != null
      ? `${pnlAmount >= 0 ? '+' : '−'}₹${Math.abs(Math.round(pnlAmount)).toLocaleString('en-IN')}`
      : null;
    const preLen      = selected.preLen || 30;
    const rr = tradePlan.sl && tradePlan.target
      ? Math.abs((tradePlan.target - tradePlan.entry) / (tradePlan.entry - tradePlan.sl))
      : null;

    // Compute whether the user's specific SL/Target was hit in the outcome candles
    const isIntraday = selected.trade_type === 'INTRADAY';
    const userPlanOutcome = (() => {
      if (tradePlan.decision !== 'ENTER' || !tradePlan.sl || !tradePlan.target) return null;
      const outcomeCandles = allCandles.slice(preLen);
      for (let i = 0; i < outcomeCandles.length; i++) {
        const c = outcomeCandles[i];
        if (c.high >= Number(tradePlan.target))
          return { type: 'TARGET', day: i + 1, price: tradePlan.target };
        if (c.low  <= Number(tradePlan.sl))
          return { type: 'STOP',   day: i + 1, price: tradePlan.sl };
      }
      const lastClose = outcomeCandles[outcomeCandles.length - 1]?.close;
      const driftPct = lastClose && tradePlan.entry
        ? ((lastClose - tradePlan.entry) / tradePlan.entry * 100).toFixed(1)
        : null;
      return { type: 'HOLD', day: outcomeCandles.length, driftPct };
    })();
    const dayLabel = (n) => isIntraday ? `min ${n}` : `day ${n}`;

    // Generic R:R advisory — shown regardless of outcome, since a low R:R carries
    // real risk even on a winning trade (and a high R:R doesn't guarantee a win).
    // Every ACT trade uses a fixed ~₹25K position so R:R is comparable across
    // scenarios. The question isn't "is ₹25K too big for your account" — it's
    // "given this ₹25K position, did the SL distance you chose keep the risk
    // within your own per-trade %" — a self-contained sizing-discipline check.
    const rrAdvisory = (() => {
      if (rr == null) return null;
      const rrLine = rr < 1.0
        ? `R:R was ${rr.toFixed(1)}:1 — below 1:1, risking more than the potential reward.`
        : rr < 1.5
        ? `R:R was ${rr.toFixed(1)}:1 — on the thin side, more exposed to normal noise/slippage.`
        : rr < 2.0
        ? `R:R was ${rr.toFixed(1)}:1 — acceptable; 2:1+ gives more room for error.`
        : `R:R was ${rr.toFixed(1)}:1 — healthy cushion for this setup.`;
      const rrColor = rr < 1.0 ? '#ef4444' : rr < 2.0 ? '#f59e0b' : '#16C784';

      let sizingLine = null;
      if (liveRiskAmount != null && fixedQty && tradePlan.entry && riskProfile?.risk_per_trade_pct != null) {
        const positionValue = fixedQty * Number(tradePlan.entry);
        const budgetPct = riskProfile.risk_per_trade_pct;
        const usedPct = (liveRiskAmount / positionValue) * 100;
        const overBudget = usedPct > budgetPct;
        sizingLine = `Your SL risked ${usedPct.toFixed(1)}% of this ₹${Math.round(positionValue).toLocaleString('en-IN')} position (₹${Math.round(liveRiskAmount).toLocaleString('en-IN')}), vs your usual ${budgetPct.toFixed(1)}% per-trade limit — ${overBudget ? 'wider than your usual per-trade risk' : 'within your usual per-trade risk'}.`;
      }
      return { text: rrLine, sizingLine, color: rrColor };
    })();

    // Cross-check the reason tag(s) the user picked against what the stored
    // scenario data actually shows — factual gap, not a right/wrong judgement.
    const tagChecks = {
      trend_aligned:  { ok: (selected.trend || '').toUpperCase() === 'UPTREND', note: `Trend was recorded as ${(selected.trend || 'unknown').replace('TREND','').toLowerCase() || 'unknown'}.` },
      rsi_in_range:   { ok: selected.rsi != null && selected.rsi >= 40 && selected.rsi <= 65, note: `RSI at signal time was ${selected.rsi != null ? selected.rsi.toFixed(0) : 'unavailable'}.` },
      macd_crossover: { ok: (selected.macd_direction || '').toUpperCase() === 'BULL', note: `MACD direction was recorded as ${(selected.macd_direction || 'unavailable').toLowerCase()}.` },
      breakout_volume:{ ok: selected.volume_ratio != null && selected.volume_ratio >= 1.5, note: `Volume was ${selected.volume_ratio != null ? `${selected.volume_ratio.toFixed(1)}x` : 'unavailable'} vs average.` },
    };
    const tagMismatches = (tradePlan.decision === 'ENTER' ? (tradePlan.selectedTags || []) : [])
      .map(id => ({ id, check: tagChecks[id] }))
      .filter(t => t.check && !t.check.ok);

    return (
      <div className="flex flex-col gap-3 pb-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => { const type = selected?.trade_type || tradeType; setSelected(null); setSearchParams(p => { const n = new URLSearchParams(p); n.delete('actView'); n.delete('actScenario'); return n; }, { replace: true }); fetchDaily(type, { silent: true }); }} className="p-1.5 rounded-md"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold" style={{ color: colors.primaryText }}>
                {selected.symbol}
              </span>
              {selected.entry_price && (
                <span className="text-xs font-semibold" style={{ color: colors.secondaryText }}>
                  ₹{selected.entry_price.toFixed(2)}
                </span>
              )}
            </div>
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>
              {formatDate(selected.signal_date)}
            </p>
          </div>
        </div>

        {/* Chart */}
        {scenarioLoading ? (
          <div className="h-64 rounded-xl animate-pulse" style={{ backgroundColor: colors.inputBg }} />
        ) : allCandles.length > 0 ? (() => {
          // For ENTER: find the day the user's own SL/Target was hit; fall back to baseline exit_day
          const userExitDay = (() => {
            if (tradePlan.decision !== 'ENTER' || !tradePlan.sl || !tradePlan.target) return null;
            const outcomeCandles = allCandles.slice(preLen);
            for (let i = 0; i < outcomeCandles.length; i++) {
              const c = outcomeCandles[i];
              if (c.high >= Number(tradePlan.target) || c.low <= Number(tradePlan.sl))
                return i + 1;
            }
            return null;
          })();
          const effectiveExitDay = userExitDay ?? selected.exit_day;
          const exitIdx = effectiveExitDay != null ? preLen + effectiveExitDay - 1 : allCandles.length - 1;
          const revealCandles = showFullChart ? allCandles : allCandles.slice(0, exitIdx + 1);
          const isRevealing   = phase === 'reveal' || phase === 'done';

          return (
            <div data-tour="act-chart" className="space-y-1">
              {/* Chart timeframe badge */}
              <div className="flex items-center gap-2 px-1 flex-wrap">
                <span className="text-[11px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide"
                  style={{ backgroundColor: colors.inputBg, color: colors.secondaryText }}>
                  {selected.trade_type === 'INTRADAY' ? '1-min candles' : 'Daily candles'}
                </span>
                {phase === 'pre' ? (
                  <span className="text-[11px]" style={{ color: colors.secondaryText }}>
                    {selected.trade_type === 'INTRADAY'
                      ? <>Chart shows session up to <span style={{ color: colors.primaryText, fontWeight: 600 }}>{selected.signal_time?.slice(0,5)}</span> — signal triggered at this point · Make your call</>
                      : <>{preLen} daily candles shown · Signal date is <span style={{ color: colors.primaryText, fontWeight: 600 }}>{selected.signal_date}</span> — the arrow marks the entry candle</>
                    }
                  </span>
                ) : (
                  <span className="text-[11px]" style={{ color: colors.secondaryText }}>
                    Full outcome revealed · Signal date: <span style={{ color: colors.primaryText, fontWeight: 600 }}>{selected.signal_date}</span>
                  </span>
                )}
              </div>
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: colors.border }}>
                <TradingChart
                  key={selected.id}
                  symbol={selected.symbol}
                  height={380}
                  actMode={true}
                  preloadedCandles={phase === 'pre' ? allCandles.slice(0, preLen) : revealCandles}
                  startFromIndex={phase !== 'pre' && !showFullChart ? preLen : null}
                  actStaticMode={phase === 'pre' || phase === 'answered' || (isRevealing && showFullChart)}
                  hideOutcome={phase === 'pre'}
                  onActComplete={() => phase === 'reveal' && setPhase('done')}
                  actEntryPrice={selected.entry_price ?? null}
                  actExitPrice={userAnswer === 'Enter' && isRevealing ? allCandles[exitIdx]?.close : undefined}
                  actKeyLevel={selected.context_json?.key_level ?? null}
                  actKeyLevelLabel={selected.context_json?.key_level_label ?? null}
                  actStopLoss={userAnswer === 'Enter' && tradePlan.sl ? tradePlan.sl : null}
                  actTargetPrice={userAnswer === 'Enter' && tradePlan.target ? tradePlan.target : null}
                  defaultIndicators={[]}
                  showBuySell={false}
                />
              </div>
              {/* "See what happened after" toggle ? only after reveal */}
              {isRevealing && selected.exit_type !== 'HOLD' && (
                <button
                  onClick={() => setShowFullChart(v => !v)}
                  className="w-full py-1.5 rounded-lg text-xs font-medium border transition-all"
                  style={{
                    borderColor: colors.border,
                    color: showFullChart ? colors.accent : colors.secondaryText,
                    backgroundColor: 'transparent',
                  }}
                >
                  {showFullChart
                    ? 'Hide full chart'
                    : selected.trade_type === 'INTRADAY'
                      ? `See what happened after min ${selected.exit_day}`
                      : `See what happened after day ${selected.exit_day}`}
                </button>
              )}
            </div>
          );
        })() : null}

        {/* Technical Context Panel · visible before and after answering */}
        {(phase === 'pre' || phase === 'answered') && (
          <>
            <div data-tour="act-context-panel">
              <ScenarioContext s={selected} context={selected.context} revealed={false} />
            </div>
          </>
        )}

        {/* Question ? phase: pre */}
        {phase === 'pre' && (
          <div data-tour="act-decision-form" className="app-card rounded-2xl border p-4 space-y-3"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderColor: '#f59e0b40' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#f59e0b' }}>
              Your Decision
            </p>
            <p className="text-sm font-medium leading-snug whitespace-pre-line" style={{ color: colors.primaryText }}>
              {selected.question}
            </p>
            <div className="flex gap-2 flex-wrap">
              {(selected.options || ['Enter', 'Skip']).map(opt => (
                <button
                  key={opt}
                  onClick={() => handleAnswer(opt)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all"
                  style={{
                    backgroundColor: colors.inputBg,
                    color: colors.primaryText,
                    borderColor: colors.border,
                    minWidth: 80,
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Answered ? show answer */}
        {phase === 'answered' && (
          <div className="app-card rounded-2xl border p-4 space-y-3"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderColor: colors.accentBorder }}>
            <p className="text-xs" style={{ color: colors.secondaryText }}>
              Your decision: <span className="font-bold" style={{ color: colors.primaryText }}>{userAnswer}</span>
            </p>

            {/* If WAIT or SKIP ? tag chips + instant submit */}
            {userAnswer !== 'Enter' && (
              <>
                {/* Change Decision */}
                <button
                  onClick={() => { setUserAnswer(null); setPhase('pre'); setTradePlan(p => ({ ...p, selectedTags: [] })); }}
                  className="text-[14px] px-3 py-1.5 rounded-lg border transition-all"
                  style={{ borderColor: colors.border, color: colors.secondaryText, backgroundColor: 'transparent' }}>
                  Change Decision
                </button>

                {/* Reference levels */}
                {(() => {
                  const isIntraday = selected.trade_type === 'INTRADAY';
                  const tgtPct = (selected.target_pct ?? (isIntraday ? 3 : 8)) / 100;
                  const slPct  = (selected.stop_pct   ?? (isIntraday ? 1.5 : 4)) / 100;
                  const tgtLabel = `+${selected.target_pct ?? (isIntraday ? 3 : 8)}%`;
                  const slLabel  = `−${selected.stop_pct ?? (isIntraday ? 1.5 : 4)}%`;
                  const unitLabel = isIntraday ? 'intraday session' : '15-day window';
                  return (
                    <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: colors.inputBg }}>
                      <p className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>
                        Reference levels (will show on chart)
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[12px]" style={{ color: colors.secondaryText }}>SL reference ({slLabel})</p>
                          <p className="text-sm font-bold" style={{ color: '#ef4444' }}>₹{(selected.entry_price * (1 - slPct)).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[12px]" style={{ color: colors.secondaryText }}>Target reference ({tgtLabel})</p>
                          <p className="text-sm font-bold" style={{ color: '#16C784' }}>₹{(selected.entry_price * (1 + tgtPct)).toFixed(2)}</p>
                        </div>
                      </div>
                      <p className="text-[11px] leading-snug" style={{ color: colors.secondaryText }}>
                        Outcome scoring uses default assumptions: {tgtLabel} target / {slLabel} stop loss over the {unitLabel}. Your verdict is based on these defaults.
                      </p>
                    </div>
                  );
                })()}

                {/* Tag chip selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>
                      Why are you skipping?
                    </label>
                    <span className="text-[12px]" style={{ color: tradePlan.selectedTags?.length >= 3 ? colors.accent : colors.secondaryText }}>
                      {tradePlan.selectedTags?.length || 0}/3 selected
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SKIP_TAGS.map(tag => {
                      const isSelected = tradePlan.selectedTags?.includes(tag.id);
                      const isDisabled = !isSelected && (tradePlan.selectedTags?.length || 0) >= 3;
                      return (
                        <button
                          key={tag.id}
                          onClick={() => {
                            if (isDisabled) return;
                            const current = tradePlan.selectedTags || [];
                            const next = isSelected
                              ? current.filter(t => t !== tag.id)
                              : [...current, tag.id];
                            setTradePlan({ ...tradePlan, selectedTags: next, reason: next.join(',') });
                          }}
                          className="px-3 py-1.5 rounded-full text-[14px] font-medium border transition-all"
                          style={{
                            borderColor: isSelected ? colors.accent : colors.border,
                            backgroundColor: isSelected ? `${colors.accent}20` : 'transparent',
                            color: isSelected ? colors.accent : isDisabled ? colors.border : colors.primaryText,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.4 : 1,
                          }}>
                          {tag.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {getPlanError() && (
                  <div className="px-3 py-2 rounded-lg text-[14px] font-medium"
                    style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
                    ⚠ {getPlanError()}
                  </div>
                )}

                <button
                  onClick={() => submitTradePlan(true)}
                  disabled={!!getPlanError()}
                  className="w-full py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#f59e0b', color: '#fff' }}
                >
                  Submit & Reveal Outcome
                </button>
              </>
            )}
          </div>
        )}

        {/* ACT Trade Planning Form (ONLY for ENTER decisions, BEFORE reveal) */}
        {phase === 'answered' && userAnswer === 'Enter' && (
          <div className="app-card rounded-2xl border p-4 space-y-4"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderColor: colors.accentBorder }}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: colors.accent }}>
                ACT: Plan Your Trade
              </p>
              <div className="flex items-center gap-2 text-right">
                <div className="text-[13px] px-2.5 py-1 rounded-lg"
                  style={{ backgroundColor: colors.inputBg }}>
                  <div className="text-[11px]" style={{ color: colors.secondaryText }}>Invest</div>
                  <div className="font-bold" style={{ color: colors.primaryText }}>₹25,000</div>
                </div>
                {liveRiskAmount !== null && (
                  <div className="text-[13px] px-2.5 py-1 rounded-lg"
                    style={{
                      backgroundColor: liveRiskPct !== null && liveRiskPct > 3 ? '#ef444420' : '#f59e0b15',
                      border: `1px solid ${liveRiskPct !== null && liveRiskPct > 3 ? '#ef444440' : '#f59e0b30'}`
                    }}>
                    <div className="text-[11px]" style={{ color: colors.secondaryText }}>Risk</div>
                    <div className="font-bold" style={{ color: liveRiskPct !== null && liveRiskPct > 3 ? '#ef4444' : '#f59e0b' }}>
                      ₹{liveRiskAmount.toLocaleString('en-IN')}
                      {liveRiskPct !== null && ` (${liveRiskPct.toFixed(1)}%)`}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Decision (locked to ENTER) */}
            <div>
              <label className="text-xs font-medium" style={{ color: colors.secondaryText }}>Decision</label>
              <div className="py-2.5 px-3 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#16C78420', borderColor: '#16C784', border: '1px solid', color: '#16C784' }}>
                ENTER at ₹{selected.entry_price?.toFixed(2) || '--'}
              </div>
            </div>

            {/* ?? Step 1: Entry (auto-filled) ???????????????????????????? */}
            <div className="rounded-lg p-3" style={{ backgroundColor: colors.inputBg }}>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>Entry Price</span>
                <span className="text-xs font-bold" style={{ color: '#16C784' }}>
                  ₹{(tradePlan.entry || selected.entry_price || 0).toFixed(2)}
                </span>
              </div>
              <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>Auto-filled from signal date close</p>
            </div>

            {/* ?? Step 2: Stop Loss ?????????????????????????????????????? */}
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>Stop Loss</label>
              <input type="number" step="0.01"
                value={tradePlan.sl || ''}
                onChange={(e) => setTradePlan({ ...tradePlan, sl: parseFloat(e.target.value) || null })}
                onWheel={(e) => e.target.blur()}
                placeholder="₹ below entry"
                className="w-full px-3 py-2 rounded-lg text-sm border"
                style={{ borderColor: selected?.trade_type !== 'INTRADAY' && tradePlan.sl && tradePlan.sl >= tradePlan.entry ? '#ef4444' : colors.border, backgroundColor: colors.inputBg, color: colors.primaryText }} />

            </div>

            {/* ?? Step 3: Target ????????????????????????????????????????? */}
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>Target</label>
              <input type="number" step="0.01"
                value={tradePlan.target || ''}
                onChange={(e) => setTradePlan({ ...tradePlan, target: parseFloat(e.target.value) || null })}
                onWheel={(e) => e.target.blur()}
                placeholder="₹ above entry"
                className="w-full px-3 py-2 rounded-lg text-sm border"
                style={{ borderColor: selected?.trade_type !== 'INTRADAY' && tradePlan.target && tradePlan.target <= tradePlan.entry ? '#ef4444' : colors.border, backgroundColor: colors.inputBg, color: colors.primaryText }} />

              {/* Live R:R badge */}
              {liveRR !== null && (
                <div className="flex items-center justify-between rounded-lg px-3 py-2"
                  style={{
                    backgroundColor: liveRR >= 2 ? '#16C78415' : liveRR >= 1.5 ? '#f59e0b15' : '#ef444415',
                    border: `1px solid ${liveRR >= 2 ? '#16C78440' : liveRR >= 1.5 ? '#f59e0b40' : '#ef444440'}`
                  }}>
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>
                    {liveRR >= 2 ? '✓ Good R:R' : liveRR >= 1.5 ? '~ Weak R:R' : '✗ Below 2:1 minimum'}
                  </span>
                  <span className="text-sm font-bold" style={{ color: liveRR >= 2 ? '#16C784' : liveRR >= 1.5 ? '#f59e0b' : '#ef4444' }}>
                    1:{liveRR.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Step 4: Fixed ₹25,000 position */}
            <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: colors.inputBg }}>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>Position Size</span>
                <span className="text-xs font-bold" style={{ color: colors.accent }}>₹25,000 fixed</span>
              </div>
              {fixedQty && (
                <p className="text-[14px]" style={{ color: colors.primaryText }}>
                  Buying <strong>{fixedQty} shares</strong> @ ₹{(tradePlan.entry || selected?.entry_price || 0).toFixed(2)}
                </p>
              )}
              {liveRiskAmount !== null && (
                <p className="text-[13px]" style={{ color: liveRiskPct !== null && liveRiskPct > (riskProfile?.risk_per_trade_pct || 2) * 2 ? '#ef4444' : colors.secondaryText }}>
                  Risk on this trade: <strong>₹{liveRiskAmount.toLocaleString('en-IN')}</strong>
                  {liveRiskPct !== null && ` (${liveRiskPct.toFixed(1)}% of account)`}
                </p>
              )}
            </div>

            {/* Conviction */}
            <div>
              <label className="text-[13px] font-medium" style={{ color: colors.secondaryText }}>How strong is this setup?</label>
              <div className="flex gap-2 mt-1">
                {[
                  { value: 'LOW',    label: 'Not sure',    desc: 'I see the pattern but not fully convinced' },
                  { value: 'MEDIUM', label: 'Looks good',  desc: 'Setup checks most of my criteria' },
                  { value: 'HIGH',   label: 'Very strong', desc: 'All signals aligned, high confidence' },
                ].map(({ value, label, desc }) => (
                  <button key={value}
                    onClick={() => setTradePlan({...tradePlan, conviction: value})}
                    className="flex-1 py-2 px-1 rounded-lg text-center transition-all border"
                    title={desc}
                    style={{
                      backgroundColor: tradePlan.conviction === value ? colors.accent : 'transparent',
                      borderColor: tradePlan.conviction === value ? colors.accent : colors.border,
                      color: tradePlan.conviction === value ? '#fff' : colors.primaryText
                    }}>
                    <div className="text-[14px] font-semibold">{label}</div>
                    <div className="text-[11px] mt-0.5 leading-tight opacity-70">{desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Why this entry ? tag chips */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[13px] font-medium" style={{ color: colors.secondaryText }}>Why are you entering?</label>
                <span className="text-[12px]" style={{ color: tradePlan.selectedTags?.length >= 3 ? colors.accent : colors.secondaryText }}>
                  {tradePlan.selectedTags?.length || 0}/3 selected
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {ENTER_TAGS.map(tag => {
                  const isSelected = tradePlan.selectedTags?.includes(tag.id);
                  const isDisabled = !isSelected && (tradePlan.selectedTags?.length || 0) >= 3;
                  return (
                    <button key={tag.id}
                      onClick={() => {
                        if (isDisabled) return;
                        const current = tradePlan.selectedTags || [];
                        const next = isSelected ? current.filter(t => t !== tag.id) : [...current, tag.id];
                        setTradePlan({ ...tradePlan, selectedTags: next });
                      }}
                      className="px-3 py-1.5 rounded-full text-[14px] font-medium border transition-all"
                      style={{
                        borderColor: isSelected ? '#16C784' : colors.border,
                        backgroundColor: isSelected ? '#16C78420' : 'transparent',
                        color: isSelected ? '#16C784' : isDisabled ? colors.border : colors.primaryText,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.4 : 1,
                      }}>
                      {tag.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Inline validation error */}
            {getPlanError() && (
              <div className="px-3 py-2 rounded-lg text-[14px] font-medium"
                style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
                ⚠ {getPlanError()}
              </div>
            )}

            {/* Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setUserAnswer(null); setPhase('pre'); setTradePlan(p => ({ ...p, selectedTags: [] })); }}
                className="py-2.5 rounded-lg text-sm font-semibold border"
                style={{ borderColor: colors.border, color: colors.secondaryText, backgroundColor: 'transparent' }}>
                Change Decision
              </button>
              <button
                onClick={() => submitTradePlan(true)}
                disabled={!!getPlanError()}
                className="py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.accent, color: '#fff' }}>
                Submit & Reveal Outcome
              </button>
            </div>
          </div>
        )}

        {/* Section context shown again after reveal — setup_type now visible */}
        {(phase === 'reveal' || phase === 'done') && (
          <ScenarioContext s={selected} context={selected.context} revealed={true} />
        )}

        {/* Reveal — single scrollable page */}
        {(phase === 'reveal' || phase === 'done') && (() => {
          const scoreColor = (s) =>
            s >= 80 ? '#16C784' : s >= 60 ? colors.accent : s >= 40 ? '#f59e0b' : '#ef4444';

          const ir = immediateResult;

          return (
            <div className="space-y-3">

              {/* ── 1. OUTCOME HEADLINE ─────────────────────────────────────── */}
              {(() => {
                const cardColor = pctColor === colors.secondaryText ? colors.accent : pctColor;
                return (
              <div className="app-card rounded-2xl border overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${cardColor}18 0%, ${cardColor}06 100%)`, borderColor: `${cardColor}35` }}>

                {/* Big stat row: win/loss word + P&L number/₹ + exit-timing tag */}
                <div className="flex items-center justify-between px-4 pt-4 pb-3">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <BarChart2 className="w-3.5 h-3.5" style={{ color: cardColor }} />
                      <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>
                        {(() => {
                          const exitType = userPlanOutcome?.type || selected.exit_type;
                          if (!exitType) return 'Outcome';
                          if (exitType === 'TARGET') return 'Target Hit';
                          if (exitType === 'STOP') return 'Stop Hit';
                          return isIntraday ? 'Held to Close' : 'Held to Window End';
                        })()}
                      </span>
                    </div>
                    <span className="text-[12px] px-2 py-0.5 rounded-full font-semibold"
                      style={{
                        backgroundColor: (userPlanOutcome?.type || selected.exit_type) === 'TARGET' ? '#16C78420' : (userPlanOutcome?.type || selected.exit_type) === 'STOP' ? '#ef444420' : '#94a3b820',
                        color:           (userPlanOutcome?.type || selected.exit_type) === 'TARGET' ? '#16C784'   : (userPlanOutcome?.type || selected.exit_type) === 'STOP' ? '#ef4444'   : '#94a3b8',
                      }}>
                      {userPlanOutcome ? (
                        userPlanOutcome.type === 'TARGET' ? `Target hit · closed ${dayLabel(userPlanOutcome.day)}`
                        : userPlanOutcome.type === 'STOP' ? `Stop hit · closed ${dayLabel(userPlanOutcome.day)}`
                        : `Held to ${isIntraday ? 'session close' : 'window end'} · ${dayLabel(userPlanOutcome.day)}`
                      ) : selected.exit_type ? (
                        selected.exit_type === 'TARGET' ? `Baseline target · ${dayLabel(selected.exit_day)}` : selected.exit_type === 'STOP' ? `Baseline stop · ${dayLabel(selected.exit_day)}` : `Held ${dayLabel(selected.exit_day)}`
                      ) : 'No exit in window'}
                    </span>
                  </div>
                  <div className="text-right">
                    {pctStr && <div className="text-[28px] font-bold tabular-nums leading-none" style={{ color: pctColor }}>{pctStr}</div>}
                    {pnlAmountStr && <div className="text-[13px] font-semibold tabular-nums mt-0.5" style={{ color: pctColor }}>{pnlAmountStr}</div>}
                  </div>
                </div>

                <div className="h-px" style={{ background: `${cardColor}20` }} />

                {/* What happened — plain fact */}
                <p className="text-[13px] leading-relaxed px-4 pt-3" style={{ color: colors.primaryText }}>
                  {userPlanOutcome ? (
                    userPlanOutcome.type === 'TARGET'
                      ? `Your target of ₹${Number(tradePlan.target).toFixed(2)} was hit on ${dayLabel(userPlanOutcome.day)}.`
                      : userPlanOutcome.type === 'STOP'
                      ? `Your stop loss of ₹${Number(tradePlan.sl).toFixed(2)} was hit on ${dayLabel(userPlanOutcome.day)}.`
                      : `Price didn't reach your target or stop in ${isIntraday ? `${userPlanOutcome.day} mins` : `${userPlanOutcome.day} days`}${userPlanOutcome.driftPct ? ` (drifted ${userPlanOutcome.driftPct}%)` : ''}.`
                  ) : selected.verdict}
                </p>

                {/* Stat pills: R:R, setup direction — compact, scannable */}
                <div className="flex flex-wrap gap-1.5 px-4 pt-2.5 pb-3">
                  {rrAdvisory && (
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: `${rrAdvisory.color}18`, color: rrAdvisory.color, border: `1px solid ${rrAdvisory.color}30` }}>
                      R:R {rr.toFixed(1)}:1
                    </span>
                  )}
                  {selected.direction && (
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize"
                      style={{ background: `${colors.secondaryText}15`, color: colors.secondaryText }}>
                      {selected.direction} · {selected.pattern_type ? selected.pattern_type.replace(/_/g, ' ').toLowerCase() : 'setup'}
                    </span>
                  )}
                </div>

                {/* Advisory detail line — the "why" behind the R:R pill */}
                {rrAdvisory && (
                  <div className="px-4 pb-3 space-y-1">
                    <p className="text-[12px] leading-relaxed" style={{ color: rrAdvisory.color }}>
                      {rrAdvisory.text}
                    </p>
                    {rrAdvisory.sizingLine && (
                      <p className="text-[12px] leading-relaxed" style={{ color: colors.secondaryText }}>
                        {rrAdvisory.sizingLine}
                      </p>
                    )}
                  </div>
                )}

                {/* Reason-tag vs actual data — factual gap, not a verdict */}
                {tagMismatches.length > 0 && (
                  <div className="mx-4 mb-3 rounded-lg px-3 py-2 space-y-1" style={{ background: `${colors.secondaryText}10` }}>
                    <p className="text-[11px] font-semibold" style={{ color: colors.secondaryText }}>Worth noting</p>
                    {tagMismatches.map(({ id, check }) => (
                      <p key={id} className="text-[12px]" style={{ color: colors.secondaryText }}>
                        You cited "{ENTER_TAGS.find(t => t.id === id)?.label || id}" — {check.note}
                      </p>
                    ))}
                  </div>
                )}

                {/* Baseline comparison — only relevant when user skipped (no plan of their own to judge) */}
                {!userPlanOutcome && selected.exit_type && (
                  <p className="text-[11px] italic px-4 pb-4" style={{ color: colors.secondaryText }}>
                    Scenario baseline ({isIntraday ? '+3% / −1.5%' : '+8% / −4%'}): {
                      selected.exit_type === 'TARGET'
                        ? `Baseline target hit on ${dayLabel(selected.exit_day)} (${isIntraday ? '+3%' : '+8%'}).`
                        : selected.exit_type === 'STOP'
                        ? `Baseline stop hit on ${dayLabel(selected.exit_day)} (${Number(baselinePct).toFixed(1)}%).`
                        : `No baseline exit in window (${Number(baselinePct) >= 0 ? '+' : ''}${Number(baselinePct).toFixed(1)}%).`
                    }
                  </p>
                )}
              </div>
                );
              })()}

              {/* ── 2. SCORE & PLAN REVIEW (one consolidated card) ──────────── */}
              {ir && (
                <div className="app-card rounded-2xl border overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: `${colors.accent}30` }}>

                  {/* Decision Quality Score */}
                  <div className="px-4 pt-4 pb-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Gauge className="w-3.5 h-3.5" style={{ color: colors.accent }} />
                      <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Decision Quality Score</span>
                    </div>
                    <div className="flex items-end gap-3">
                      <span className="text-[28px] font-bold leading-none tabular-nums" style={{ color: scoreColor(ir.score) }}>
                        {ir.score}
                      </span>
                      <span className="text-[13px] font-semibold mb-0.5" style={{ color: scoreColor(ir.score) }}>
                        {ir.label}
                      </span>
                    </div>
                    {ir.flags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {ir.flags.map(f => (
                          <span key={f} className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: '#ef444418', color: '#ef4444', border: '1px solid #ef444430' }}>
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Trade Quality at-a-glance — R:R / SL / Target / Size, pulled from the same
                      plan_challenge items as the checklist below, just condensed to badges */}
                  {ir.plan_challenge?.items?.length > 0 && (() => {
                    const wanted = ['R:R Ratio', 'Stop Loss', 'Target', 'Position Size'];
                    const badges = wanted
                      .map(field => ir.plan_challenge.items.find(it => it.field === field))
                      .filter(Boolean);
                    if (badges.length === 0) return null;
                    const statusColor = (s) => s === 'good' ? '#16C784' : s === 'caution' ? '#f59e0b' : '#ef4444';
                    return (
                      <>
                        <div className="h-px" style={{ background: `${colors.accent}20` }} />
                        <div className="px-4 py-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: colors.secondaryText }}>
                            Trade Quality
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {badges.map(b => (
                              <div key={b.field} className="rounded-lg px-2.5 py-2"
                                style={{ backgroundColor: `${statusColor(b.status)}12`, border: `1px solid ${statusColor(b.status)}30` }}>
                                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>
                                  {b.field}
                                </p>
                                <p className="text-[13px] font-bold mt-0.5" style={{ color: statusColor(b.status) }}>
                                  {b.value || '—'}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* Plan Quality checklist */}
                  {ir.plan_challenge?.items?.length > 0 && (
                    <>
                      <div className="h-px" style={{ background: `${colors.accent}20` }} />
                      <div className="px-4 py-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>
                            Plan Quality
                          </p>
                          {ir.plan_challenge.plan_score != null && (
                            <span className="text-[12px] font-bold" style={{ color: scoreColor(ir.plan_challenge.plan_score) }}>
                              {ir.plan_challenge.plan_score}/100
                            </span>
                          )}
                        </div>
                        {ir.plan_challenge.items.map((item, i) => (
                          <div key={i} className="flex items-start gap-2.5">
                            <span className="text-[13px] mt-0.5 shrink-0 font-bold"
                              style={{ color: item.status === 'good' ? '#16C784' : item.status === 'caution' ? '#f59e0b' : '#ef4444' }}>
                              {item.status === 'good' ? '✓' : item.status === 'caution' ? '~' : '✗'}
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                                <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
                                  {item.field}
                                </span>
                                {item.value && (
                                  <span className="text-[11px] px-1.5 py-0.5 rounded font-mono"
                                    style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}>
                                    {item.value}
                                  </span>
                                )}
                              </div>
                              <p className="text-[12px] leading-relaxed" style={{ color: colors.secondaryText }}>
                                {item.note}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Reasoning Tags */}
                  {(ir.valid_tags?.length > 0 || ir.contradictions?.length > 0) && (
                    <>
                      <div className="h-px" style={{ background: `${colors.accent}20` }} />
                      <div className="px-4 py-3 space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>
                          Your Reasoning Tags
                        </p>
                        {ir.valid_tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {ir.valid_tags.map(t => (
                              <span key={t} className="text-[12px] px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: '#16C78418', color: '#16C784', border: '1px solid #16C78430' }}>
                                ✓ {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {ir.contradictions?.length > 0 && (
                          <div className="space-y-1.5">
                            {ir.contradictions.map((c, i) => (
                              <div key={i} className="rounded-lg px-2.5 py-1.5"
                                style={{ backgroundColor: '#f59e0b10', border: '1px solid #f59e0b25' }}>
                                <p className="text-[12px] leading-snug" style={{ color: '#f59e0b' }}>
                                  <span className="font-semibold">{c.tag}:</span> {c.explanation}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Key Lesson */}
                  {ir.tip && (
                    <>
                      <div className="h-px" style={{ background: `${colors.accent}20` }} />
                      <div className="px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: colors.accent }}>
                          Key Lesson
                        </p>
                        <p className="text-[13px] leading-relaxed" style={{ color: colors.primaryText }}>
                          {ir.tip}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── 9. NEXT / BACK ──────────────────────────────────────────── */}
              {(() => {
                const doneIds = new Set((history?.recent_trades || []).map(h => h.scenario_id));
                const next = scenarios.find(s => s.id !== selected.id && !doneIds.has(s.id));
                return next ? (
                  <button onClick={() => { fetchDaily(selected?.trade_type || tradeType, { silent: true }); openScenario(next); }}
                    className="w-full py-2.5 rounded-xl text-sm font-bold transition-all"
                    style={{ backgroundColor: colors.accent, color: '#fff' }}>
                    Next Scenario
                  </button>
                ) : (
                  <button onClick={() => { fetchDaily(selected?.trade_type || tradeType); setSelected(null); }}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold border"
                    style={{ borderColor: colors.border, color: colors.secondaryText, backgroundColor: 'transparent' }}>
                    Back to Scenarios
                  </button>
                );
              })()}
            </div>
          );
        })()}

        {/* ACT Trade Planning Form (appears after reveal) */}
      </div>
    );
  }

  // ?? History Dashboard ????????????????????????????????????????????????????
  if (view === 'history') {
    return (
      <div className="flex flex-col gap-3 pb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('list')} className="p-1.5 rounded-md"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h2 className="text-lg font-bold" style={{ color: colors.primaryText }}>ACT Trade History</h2>
            <p className="text-xs" style={{ color: colors.secondaryText }}>Your decision quality scores</p>
          </div>
        </div>

        {history && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-2">
              <div className="app-card rounded-2xl p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Activity className="w-3.5 h-3.5" style={{ color: colors.accent }} />
                  <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Total Decisions</span>
                </div>
                <p className="text-[22px] font-bold tabular-nums" style={{ color: colors.primaryText }}>{history.decisions_made}</p>
              </div>
              <div className="app-card rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, #16C78418 0%, #16C78406 100%)', border: '1px solid #16C78435' }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Gauge className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
                  <span className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>Avg Quality Score</span>
                </div>
                <p className="text-[22px] font-bold tabular-nums" style={{ color: '#16C784' }}>{history.quality_stats?.avg_decision_quality?.toFixed(0) || '?'}<span className="text-[13px] font-medium" style={{ color: colors.secondaryText }}>/100</span></p>
              </div>
            </div>

            {/* Decision Breakdown */}
            <div className="app-card rounded-2xl p-4 space-y-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
              <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Decision Breakdown</p>
              <div className="space-y-2">
                {[
                  { label: 'Enters', value: history.breakdown?.enter, color: '#16C784' },
                  { label: 'Skips', value: history.breakdown?.skip, color: '#ef4444' }
                ].map(d => (
                  <div key={d.label} className="flex items-center gap-2">
                    <span className="text-[12px] font-medium w-14" style={{ color: colors.secondaryText }}>{d.label}</span>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.inputBg }}>
                      <div style={{
                        width: `${history.decisions_made > 0 ? (d.value / history.decisions_made * 100) : 0}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${d.color}80, ${d.color})`,
                      }} />
                    </div>
                    <span className="text-[13px] font-bold w-14 text-right tabular-nums" style={{ color: d.color }}>
                      {d.value} <span className="text-[11px] font-medium" style={{ color: colors.secondaryText }}>({history.decisions_made > 0 ? ((d.value / history.decisions_made * 100).toFixed(0)) : 0}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Trades */}
            {history.recent_trades && history.recent_trades.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Recent Trades</p>
                {history.recent_trades.slice(0, 10).map((t, i) => {
                  const scoreColor = t.decision_quality_score >= 80 ? '#16C784' : t.decision_quality_score >= 60 ? '#f59e0b' : '#ef4444';
                  return (
                  <div key={i} className="app-card rounded-2xl p-3.5 cursor-pointer active:opacity-70 transition-all"
                    style={{ background: `linear-gradient(135deg, ${scoreColor}14 0%, ${scoreColor}05 100%)`, border: `1px solid ${scoreColor}30` }}
                    onClick={() => openPlanReview(t.plan_id)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-bold" style={{ color: colors.primaryText }}>{t.symbol}</p>
                        <p className="text-[12px] font-medium" style={{ color: colors.secondaryText }}>
                          {t.decision} · {formatDate(t.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold tabular-nums" style={{ color: scoreColor }}>{t.decision_quality_score || '?'}<span className="text-[11px] font-medium" style={{ color: colors.secondaryText }}>/100</span></p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ?? Plan Review ??????????????????????????????????????????????????????????
  if (view === 'review') {
    const p = reviewPlan;
    const scoreColor = p?.score >= 80 ? '#16C784' : p?.score >= 60 ? '#f59e0b' : '#ef4444';
    const allReviewCandles = p ? [...(p.pre_candles || []), ...(p.outcome_candles || [])] : [];
    const firstTime = allReviewCandles[0]?.time;
    const reviewProductType = (typeof firstTime === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(firstTime))
      ? 'SWING'
      : (p?.trade_type === 'INTRADAY' ? 'INTRADAY' : 'SWING');
    return (
      <div className="flex flex-col gap-3 pb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('history')} className="p-1.5 rounded-md" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h2 className="text-lg font-bold" style={{ color: colors.primaryText }}>
              {p ? `${p.symbol} · Review` : 'Loading…'}
            </h2>
            {p && <p className="text-xs" style={{ color: colors.secondaryText }}>{p.signal_date} · {p.scanner_name}</p>}
          </div>
          {p && <span className="ml-auto text-lg font-bold" style={{ color: scoreColor }}>{p.score}/100</span>}
        </div>

        {reviewLoading && <p className="text-center text-sm py-8" style={{ color: colors.secondaryText }}>Loading review…</p>}

        {p && (
          <>
            {/* Scenario question */}
            <div className="app-card rounded-2xl p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, border: `1px solid ${colors.accent}35` }}>
              <p className="text-[13px] font-medium whitespace-pre-line" style={{ color: colors.primaryText }}>{p.question}</p>
              <div className="h-px my-2.5" style={{ background: `${colors.accent}20` }} />
              <div className="flex gap-4 text-[12px] font-medium" style={{ color: colors.secondaryText }}>
                {p.rsi && (
                  <span className="flex items-center gap-1"><Gauge className="w-3 h-3" style={{ color: colors.accent }} />RSI {p.rsi?.toFixed(0)}</span>
                )}
                {p.volume_ratio && (
                  <span className="flex items-center gap-1"><BarChart2 className="w-3 h-3" style={{ color: colors.accent }} />Vol {p.volume_ratio?.toFixed(1)}x</span>
                )}
                <span className="ml-auto">{p.trade_type}</span>
              </div>
            </div>

            {/* Full chart — both pre + outcome revealed */}
            {allReviewCandles.length > 0 && (
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: colors.border }}>
                <TradingChart
                  symbol={p.symbol}
                  height={340}
                  actMode={true}
                  preloadedCandles={allReviewCandles}
                  actStaticMode={true}
                  hideOutcome={false}
                  actEntryPrice={p.entry_price}
                  actStopLoss={p.stop_loss}
                  actTargetPrice={p.target}
                  actKeyLevel={p.context_json?.key_level ?? null}
                  actKeyLevelLabel={p.context_json?.key_level_label ?? null}
                  defaultIndicators={[]}
                  showBuySell={false}
                />
              </div>
            )}

            {/* User's decision */}
            <div className="app-card rounded-lg p-4 space-y-2" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
              <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>Your Decision</p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold px-3 py-1 rounded-full" style={{
                  backgroundColor: p.user_decision === 'ENTER' ? '#16C78420' : '#ef444420',
                  color: p.user_decision === 'ENTER' ? '#16C784' : '#ef4444'
                }}>{p.user_decision}</span>
                <span className="text-xs" style={{ color: colors.secondaryText }}>Conviction: {p.conviction}</span>
              </div>
              {p.user_reason && <p className="text-[13px]" style={{ color: colors.secondaryText }}>"{p.user_reason}"</p>}
              {p.user_decision === 'ENTER' && (
                <div className="grid grid-cols-3 gap-2 mt-2 text-center text-[12px]">
                  {[['Entry', p.entry_price], ['SL', p.stop_loss], ['Target', p.target]].map(([label, val]) => (
                    <div key={label} className="rounded-md p-2" style={{ backgroundColor: colors.inputBg }}>
                      <p style={{ color: colors.secondaryText }}>{label}</p>
                      <p className="font-bold" style={{ color: colors.primaryText }}>₹{val?.toFixed(0) ?? '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Outcome */}
            {p.outcome_pct != null && (
              <div className="app-card rounded-lg p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
                <p className="text-xs font-semibold mb-1" style={{ color: colors.secondaryText }}>Actual Outcome</p>
                <p className="text-sm font-bold" style={{ color: p.outcome_pct >= 0 ? '#16C784' : '#ef4444' }}>
                  {p.outcome_pct >= 0 ? '+' : ''}{p.outcome_pct?.toFixed(2)}% · {p.exit_type}
                </p>
                {p.correct_answer && <p className="text-[12px] mt-1" style={{ color: colors.secondaryText }}>Setup direction: <span className="font-bold">{p.correct_answer}</span></p>}
              </div>
            )}

            {/* Coaching feedback */}
            {p.feedback && p.feedback.length > 0 && (
              <div className="app-card rounded-lg p-4 space-y-2" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
                <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>Feedback</p>
                {p.feedback.map((f, i) => (
                  <p key={i} className="text-[13px]" style={{ color: colors.primaryText }}>· {typeof f === 'object' ? f.text || JSON.stringify(f) : f}</p>
                ))}
              </div>
            )}

            {/* Setup read */}
            {p.setup_read && (() => {
              let sr = p.setup_read;
              if (typeof sr === 'string') { try { sr = JSON.parse(sr); } catch { sr = { character: sr }; } }
              if (typeof sr !== 'object' || sr === null) return null;
              return (
                <div className="app-card rounded-lg p-4 space-y-2" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
                  <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>Setup Read</p>
                  {sr.setup_type && <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{sr.setup_type}</p>}
                  {sr.character && <p className="text-[13px] italic" style={{ color: colors.secondaryText }}>{sr.character}</p>}
                  {sr.confluence?.length > 0 && (
                    <div className="space-y-1">
                      {sr.confluence.map((c, i) => <p key={i} className="text-[12px]" style={{ color: '#16C784' }}>+ {c}</p>)}
                    </div>
                  )}
                  {sr.key_risk?.length > 0 && (
                    <div className="space-y-1">
                      {sr.key_risk.map((r, i) => <p key={i} className="text-[12px]" style={{ color: '#ef4444' }}>? {r}</p>)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Coaching tip */}
            {p.tip && (
              <div className="app-card rounded-lg p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
                <p className="text-xs font-semibold mb-1" style={{ color: colors.secondaryText }}>Coaching Tip</p>
                <p className="text-[13px]" style={{ color: colors.primaryText }}>{p.tip}</p>
              </div>
            )}

            {/* Plan challenge */}
            {p.plan_challenge && (() => {
              let pc = p.plan_challenge;
              if (typeof pc === 'string') { try { pc = JSON.parse(pc); } catch { return null; } }
              if (typeof pc !== 'object' || pc === null) return null;
              const statusColor = (s) => s === 'good' ? '#16C784' : s === 'caution' ? '#f59e0b' : '#ef4444';
              return (
                <div className="app-card rounded-lg p-4 space-y-3" style={{ backgroundColor: '#6366f110', border: '1px solid #6366f140' }}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold" style={{ color: '#a5b4fc' }}>Plan Review</p>
                    {pc.plan_score != null && <span className="text-xs font-bold" style={{ color: '#a5b4fc' }}>{pc.plan_score}/100</span>}
                  </div>
                  {pc.items?.map((item, i) => (
                    <div key={i} className="space-y-0.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] font-semibold" style={{ color: statusColor(item.status) }}>{item.field}</span>
                        <span className="text-[12px]" style={{ color: statusColor(item.status) }}>{item.value}</span>
                      </div>
                      <p className="text-[12px]" style={{ color: colors.secondaryText }}>{item.note}</p>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  }

  // ?? Scenario list ????????????????????????????????????????????????????????
  return (
    <div className="flex flex-col gap-3 pb-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="app-card p-1.5 rounded-md"
            style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h1 className="text-lg font-bold" style={{ color: colors.primaryText }}>ACT</h1>
            <p className="text-xs" style={{ color: colors.secondaryText }}>
              Plan trades · Process-based scoring
            </p>
          </div>
        </div>
        <button onClick={() => fetchDaily(tradeType)} className="p-2 rounded-lg"
          style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }}>
          <RefreshCw className="w-4 h-4" style={{ color: colors.secondaryText }} />
        </button>
      </div>

      {/* Daily Progress */}
      <div data-tour="act-daily-progress" className="app-card rounded-2xl border p-3.5 space-y-2.5"
        style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.accent}06 100%)`, borderColor: `${colors.accent}35` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5" style={{ color: colors.accent }} />
            <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>
              Today's Progress
            </p>
          </div>
          {dailyProgress.swing >= 5 && dailyProgress.intraday >= 5 && (
            <span className="text-[12px] px-2 py-0.5 rounded-full font-bold"
              style={{ backgroundColor: '#16C78420', color: '#16C784', border: '1px solid #16C78440' }}>
              ✓ All Done
            </span>
          )}
        </div>
        {[
          { label: 'Swing', count: dailyProgress.swing, max: 5 },
          { label: 'Intraday', count: dailyProgress.intraday, max: 5 },
        ].map(({ label, count, max }) => {
          const pct = Math.min(100, (count / max) * 100);
          const done = count >= max;
          return (
            <div key={label} className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-[13px] font-medium" style={{ color: colors.secondaryText }}>{label}</span>
                <span className="text-[15px] font-bold tabular-nums"
                  style={{ color: done ? '#16C784' : colors.primaryText }}>
                  {count}/{max}{done ? ' ✓' : ''}
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.inputBg }}>
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: done ? 'linear-gradient(90deg, #16C784, #16C784)' : `linear-gradient(90deg, ${colors.accent}80, ${colors.accent})` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Related Learning — collapsed banner, expands to all modules */}
      {(() => {
        const swingModules = [
          { level: 6,  label: 'Reading Charts',      sub: 'Candles, trends, support/resistance' },
          { level: 8,  label: 'Chart Patterns',      sub: 'Breakouts, flags, reversals' },
          { level: 9,  label: 'Scanner Signals',     sub: 'RSI, MACD, volume signals' },
          { level: 7,  label: 'Decision Framework',  sub: 'Trend → Event → Confirmation' },
          { level: 2,  label: 'Risk Management',     sub: 'Stop loss, position sizing' },
          { level: 14, label: 'Global Cues',         sub: 'FII, US markets, VIX, bias' },
          { level: 3,  label: 'Trader Vocabulary',   sub: 'Breakout, pullback, R:R, SL' },
        ];
        const intradayModules = [
          { level: 15, label: 'Swing vs Intraday',   sub: 'Session timing, trade style' },
          { level: 9,  label: 'Scanner Signals',     sub: 'RSI, MACD, volume signals' },
          { level: 6,  label: 'Reading Charts',      sub: 'Candles, VWAP, ORB concepts' },
          { level: 7,  label: 'Decision Framework',  sub: 'Entry confirmation, skip rules' },
          { level: 14, label: 'Global Cues',         sub: 'Gap context, open behavior' },
          { level: 2,  label: 'Risk Management',     sub: 'Intraday SL, capital rules' },
          { level: 3,  label: 'Trader Vocabulary',   sub: 'Gap up, reversal, momentum' },
        ];
        const modules = tradeType === 'INTRADAY' ? intradayModules : swingModules;
        const accent = tradeType === 'INTRADAY' ? '#f59e0b' : '#6366f1';
        return (
          <div className="app-card rounded-2xl overflow-hidden border" style={{ borderColor: `${accent}35` }}>
            {/* Header — always visible, tap to expand */}
            <button
              onClick={() => setRelatedOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left"
              style={{ background: `linear-gradient(135deg, ${accent}15 0%, ${accent}05 100%)` }}>
              <div className="flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
                <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Understand the concepts used in{' '}
                  <span style={{ color: accent }}>{tradeType === 'INTRADAY' ? 'Intraday ACT' : 'Swing ACT'}</span>
                </span>
              </div>
              <ChevronRight
                className="w-3.5 h-3.5 shrink-0 transition-transform"
                style={{ color: `${accent}80`, transform: relatedOpen ? 'rotate(90deg)' : 'rotate(0deg)' }} />
            </button>

            {/* Expanded module list */}
            {relatedOpen && (
              <div className="flex flex-col divide-y" style={{ borderTop: `1px solid ${accent}18`, divideColor: `${accent}10` }}>
                {modules.map(({ level, label, sub }) => (
                  <button key={level}
                    onClick={() => navigate(`/learning?view=level&level=${level}&card=0`)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-left"
                    style={{ background: '#ffffff04' }}>
                    <div className="flex items-center gap-2.5">
                      <span className="text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: `${accent}20`, color: accent }}>
                        {level}
                      </span>
                      <div>
                        <p className="text-[12px] font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>{label}</p>
                        <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: 'rgba(255,255,255,0.2)' }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}


      {/* Toggle */}
      <div data-tour="act-trade-type-toggle" className="app-card flex rounded-2xl overflow-hidden border p-1 gap-1"
        style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}>
        {['SWING', 'INTRADAY'].map(t => {
          const active = tradeType === t;
          const tColor = t === 'INTRADAY' ? '#f59e0b' : colors.accent;
          return (
            <button
              key={t}
              onClick={() => handleToggle(t)}
              className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
              style={active ? {
                background: `linear-gradient(135deg, ${tColor} 0%, ${tColor}cc 100%)`,
                color: '#fff',
                boxShadow: `0 2px 10px ${tColor}50`,
              } : {
                background: 'transparent',
                color: colors.secondaryText,
              }}
            >
              {t === 'SWING' ? 'Swing (15 days)' : 'Intraday (same day)'}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => (
            <div key={i} className="app-card h-16 rounded-xl animate-pulse"
              style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }} />
          ))}
        </div>
      ) : scenarios.length === 0 ? (
        <div className="text-center py-16 space-y-2">
          <Zap className="w-10 h-10 mx-auto opacity-30" style={{ color: '#f59e0b' }} />
          <p className="text-sm" style={{ color: colors.secondaryText }}>
            No scenarios yet ? check back after market hours
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" style={{ color: colors.accent }} />
            <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>Today's Scenarios</p>
          </div>
          <div data-tour="act-scenario-cards" className="space-y-2">
            {scenarios.map((s) => {
              const doneRecord = s.completed
                ? (history?.recent_trades || []).find(t => t.scenario_id === s.id)
                : null;
              const dirColor = s.direction === 'bullish' ? '#16C784' : s.direction === 'bearish' ? '#ef4444' : colors.accent;
              return (
                <button
                  key={s.id}
                  onClick={() => openScenario(s)}
                  className="app-card w-full text-left rounded-2xl border p-3 transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${dirColor}18 0%, ${dirColor}06 100%)`,
                    borderColor: s.completed ? '#16C78435' : `${dirColor}30`,
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-sm font-bold" style={{ color: colors.primaryText }}>{s.symbol}</span>
                        {dirIcon(s.direction)}
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full font-semibold"
                          style={{ backgroundColor: '#f59e0b18', color: '#f59e0b', border: '1px solid #f59e0b30' }}>
                          {s.pattern_type}
                        </span>
                        {s.completed && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ backgroundColor: '#16C78420', color: '#16C784', border: '1px solid #16C78440' }}>
                            ✓ Done · tap to review
                          </span>
                        )}
                      </div>
                      <p className="text-xs leading-snug truncate" style={{ color: colors.secondaryText }}>
                        {s.question}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {doneRecord?.decision_quality_score ? (
                        <span className="text-base font-bold tabular-nums"
                          style={{
                            color: doneRecord.decision_quality_score >= 80 ? '#16C784'
                                 : doneRecord.decision_quality_score >= 60 ? '#f59e0b' : '#ef4444'
                          }}>
                          {doneRecord.decision_quality_score}
                        </span>
                      ) : (
                        <ChevronRight className="w-4 h-4" style={{ color: colors.secondaryText }} />
                      )}
                    </div>
                  </div>
                  <div className="h-px my-2" style={{ background: `${dirColor}20` }} />
                  <div className="flex gap-3">
                    {s.rsi && (
                      <span className="flex items-center gap-1 text-[12px] font-medium" style={{ color: colors.secondaryText }}>
                        <Gauge className="w-3 h-3" style={{ color: dirColor }} />RSI {s.rsi.toFixed(0)}
                      </span>
                    )}
                    {s.volume_ratio && (
                      <span className="flex items-center gap-1 text-[12px] font-medium" style={{ color: colors.secondaryText }}>
                        <BarChart2 className="w-3 h-3" style={{ color: dirColor }} />Vol {s.volume_ratio.toFixed(1)}x
                      </span>
                    )}
                    <span className="text-[12px] font-medium ml-auto" style={{ color: colors.secondaryText }}>{s.signal_date}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
